import { DataGrid, GridCellEditStopParams, GridColDef } from "@mui/x-data-grid";
import { IPJsApi } from '../PJs/types'
import { cpfMask } from "utils/cpf";
import dayjs from "dayjs";
import { useState } from "react";
import { Box, Button, CircularProgress, IconButton, Modal, Table, TableCell, TableContainer, TableRow } from "@mui/material";
import { ArrowBack, Close } from "@mui/icons-material";
import api from "services/api";
import { useParams } from "react-router-dom";
import { ptBR } from '@mui/x-data-grid/locales';

type Props = {
  data: IPJsApi['people'];
  editableFields: string[];
  setCurrentView: (payload: 'ADDING' | 'EDITING' | 'DEFAULT') => void;
  refetchData: () => void;
}

export  function EditarDadosPj({ data, editableFields, setCurrentView, refetchData }: Props) {
  const { workspaceId } = useParams() as { workspaceId: string };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSendingUpdate, setIsSendingUpdate] = useState(false);

  const [realRows, setRealRows] = useState(data.map((row, index) => ({
    id: row?.id ?? `${index + 1}_${Date.now()}`,
    
    // Common fields
    name: row?.name ?? '',
    taxPayerId: !!row?.taxPayerId ? cpfMask(row.taxPayerId) : '',
    taxPayerIdEmployee: !!row?.employeeTaxPayerId ? cpfMask(row.employeeTaxPayerId) : '',
    birthDate: !!row?.birthDate ? new Date(row.birthDate) : null,
    motherName: row?.motherName ? row.motherName : '',
    weddingDate: !!row?.weddingDate ? new Date(row.weddingDate) : null,
    type: row?.type ?? '',
    
    // Employee specific fields
    organizationId: row?.organizationId ?? '',
    cnpj: row?.cnpj ?? '',
    pis: row?.pis ?? '',
    genderId: row?.genderId ?? '',
    raceId: row?.raceId ?? '',
    maritalStatusId: row?.maritalStatusId ?? '',
    educationLevelId: row?.educationLevelId ?? '',
    socialName: row?.socialName ?? '',
    birthCountryId: row?.birthCountryId ?? '',
    nationalityCountryId: row?.nationalityCountryId ?? '',
    birthStateId: row?.birthStateId ?? '',
    birthCityId: row?.birthCityId ?? '',
    fatherName: row?.fatherName ?? '',
    zipCode: row?.zipCode ?? '',
    addressType: row?.addressType ?? '',
    address: row?.address ?? '',
    addressNumber: row?.addressNumber ?? '',
    complement: row?.complement ?? '',
    neighborhood: row?.neighborhood ?? '',
    stateId: row?.stateId ?? '',
    cityId: row?.cityId ?? '',
    nationalIdentityNumber: row?.nationalIdentityNumber ?? '',
    nationalIdentityEmissor: row?.nationalIdentityEmissor ?? '',
    nationalIdentityExpeditionDate: !!row?.nationalIdentityExpeditionDate ? new Date(row.nationalIdentityExpeditionDate) : null,
    phoneNumber: row?.phoneNumber ?? '',
    email: row?.email ?? '',
    hiringDate: !!row?.hiringDate ? new Date(row.hiringDate) : null,
    
    // New fields from layoutFields.txt
    status: row?.status ?? '',
    jobTitle: row?.jobTitle ?? '',
    paymentFrequency: row?.paymentFrequency ?? '',
    grossPayment: row?.grossPayment ?? '',
    municipality: row?.municipality ?? '',
    cbo: row?.cbo ?? '',
    dismissalDate: !!row?.dismissalDate ? new Date(row.dismissalDate) : null,
    startPriorNoticeDate: !!row?.startPriorNoticeDate ? new Date(row.startPriorNoticeDate) : null,
    endPriorNoticeDate: !!row?.endPriorNoticeDate ? new Date(row.endPriorNoticeDate) : null,
    transferred: row?.transferred ?? '',
    plan: row?.plan ?? '',
    
    // Dependent specific fields
    employeeId: row?.employeeId ?? '',
    dependentTypeId: row?.dependentTypeId ?? '',
    isDeleted: row?.isDeleted !== null ? (row.isDeleted ? 'Sim' : 'Não') : '-',
    isStudent: row?.isStudent !== null ? (row.isStudent ? 'Sim' : 'Não') : '-',
    
    // Timestamps (mantendo '-' pois são apenas informativos)
    createdAt: !!row?.createdAt ? dayjs(row.createdAt).format("DD/MM/YYYY HH:mm") : '-',
    updatedAt: !!row?.updatedAt ? dayjs(row.updatedAt).format("DD/MM/YYYY HH:mm") : '-',
  })));
  // More flexible change tracking for all fields
  const [changes, setChanges] = useState<null | Array<{
    // id: string;`
    // name: string;
    // taxPayerId: string;
    // type: string;
    [key: string]: any; // Allow any field changes to be tracked dynamically
  }>>(null);

  const realColumns: GridColDef<(typeof realRows)[number]>[] = [
    // Common fields
    { 
      field: "name", 
      headerName: "Nome completo", 
      width: 350, 
      editable: editableFields.includes("name") 
    },
    { 
      field: "taxPayerId", 
      headerName: "CPF beneficiário", 
      width: 150,
      editable: editableFields.includes("taxPayerId")
    },
    { 
      field: "taxPayerIdEmployee", 
      headerName: "CPF titular", 
      width: 150,
      editable: editableFields.includes("taxPayerIdEmployee")
    },
    { 
      field: "birthDate", 
      headerName: "Data de nascimento", 
      width: 150,
      editable: editableFields.includes("birthDate"),
      type: "date"
    },
    { 
      field: "motherName", 
      headerName: "Nome da mãe", 
      width: 200, 
      editable: editableFields.includes("motherName")
    },
    { 
      field: "weddingDate", 
      headerName: "Data de casamento", 
      width: 150, 
      editable: editableFields.includes("weddingDate"), 
      type: "date" 
    },
    { 
      field: "type", 
      headerName: "Tipo", 
      width: 120,
      editable: editableFields.includes("type")
    },
    
    // Employee specific fields
    { 
      field: "organizationId", 
      headerName: "ID Organização", 
      width: 150,
      editable: editableFields.includes("organizationId")
    },
    { 
      field: "cnpj", 
      headerName: "CNPJ empresa", 
      width: 150,
      editable: editableFields.includes("cnpj")
    },
    { 
      field: "pis", 
      headerName: "PIS", 
      width: 130,
      editable: editableFields.includes("pis")
    },
    { 
      field: "genderId", 
      headerName: "Gênero", 
      width: 100,
      editable: editableFields.includes("genderId")
    },
    { 
      field: "raceId", 
      headerName: "Raça", 
      width: 100,
      editable: editableFields.includes("raceId")
    },
    { 
      field: "maritalStatusId", 
      headerName: "Estado Civil", 
      width: 120,
      editable: editableFields.includes("maritalStatusId")
    },
    { 
      field: "educationLevelId", 
      headerName: "Escolaridade", 
      width: 120,
      editable: editableFields.includes("educationLevelId")
    },
    { 
      field: "socialName", 
      headerName: "Nome Social", 
      width: 200,
      editable: editableFields.includes("socialName")
    },
    { 
      field: "birthCountryId", 
      headerName: "País Nascimento", 
      width: 130,
      editable: editableFields.includes("birthCountryId")
    },
    { 
      field: "nationalityCountryId", 
      headerName: "Nacionalidade", 
      width: 120,
      editable: editableFields.includes("nationalityCountryId")
    },
    { 
      field: "birthStateId", 
      headerName: "Estado Nascimento", 
      width: 130,
      editable: editableFields.includes("birthStateId")
    },
    { 
      field: "birthCityId", 
      headerName: "Cidade Nascimento", 
      width: 130,
      editable: editableFields.includes("birthCityId")
    },
    { 
      field: "fatherName", 
      headerName: "Nome do Pai", 
      width: 200,
      editable: editableFields.includes("fatherName")
    },
    { 
      field: "zipCode", 
      headerName: "CEP", 
      width: 100,
      editable: editableFields.includes("zipCode")
    },
    { 
      field: "addressType", 
      headerName: "Tipo Endereço", 
      width: 120,
      editable: editableFields.includes("addressType")
    },
    { 
      field: "address", 
      headerName: "Endereço", 
      width: 250,
      editable: editableFields.includes("address")
    },
    { 
      field: "addressNumber", 
      headerName: "Número", 
      width: 100,
      editable: editableFields.includes("addressNumber")
    },
    { 
      field: "complement", 
      headerName: "Complemento", 
      width: 150,
      editable: editableFields.includes("complement")
    },
    { 
      field: "neighborhood", 
      headerName: "Bairro", 
      width: 150,
      editable: editableFields.includes("neighborhood")
    },
    { 
      field: "stateId", 
      headerName: "Estado", 
      width: 100,
      editable: editableFields.includes("stateId")
    },
    { 
      field: "cityId", 
      headerName: "Cidade", 
      width: 130,
      editable: editableFields.includes("cityId")
    },
    { 
      field: "nationalIdentityNumber", 
      headerName: "RG", 
      width: 130,
      editable: editableFields.includes("nationalIdentityNumber")
    },
    { 
      field: "nationalIdentityEmissor", 
      headerName: "Órgão Emissor RG", 
      width: 130,
      editable: editableFields.includes("nationalIdentityEmissor")
    },
    { 
      field: "nationalIdentityExpeditionDate", 
      headerName: "Data Expedição RG", 
      width: 150,
      editable: editableFields.includes("nationalIdentityExpeditionDate"),
      type: "date"
    },
    { 
      field: "phoneNumber", 
      headerName: "Telefone", 
      width: 130,
      editable: editableFields.includes("phoneNumber")
    },
    { 
      field: "email", 
      headerName: "E-mail", 
      width: 200,
      editable: editableFields.includes("email")
    },
    { 
      field: "hiringDate", 
      headerName: "Data de admissão", 
      width: 150,
      editable: editableFields.includes("hiringDate"),
      type: "date"
    },
    
    // New fields from layoutFields.txt
    { 
      field: "status", 
      headerName: "Status", 
      width: 120,
      editable: editableFields.includes("status")
    },
    { 
      field: "jobTitle", 
      headerName: "Cargo", 
      width: 180,
      editable: editableFields.includes("jobTitle")
    },
    { 
      field: "paymentFrequency", 
      headerName: "Periodicidade Pagamento", 
      width: 180,
      editable: editableFields.includes("paymentFrequency")
    },
    { 
      field: "grossPayment", 
      headerName: "Pagamento Bruto", 
      width: 150,
      editable: editableFields.includes("grossPayment")
    },
    { 
      field: "municipality", 
      headerName: "Município", 
      width: 150,
      editable: editableFields.includes("municipality")
    },
    { 
      field: "cbo", 
      headerName: "CBO", 
      width: 100,
      editable: editableFields.includes("cbo")
    },
    {
      field: "dismissalDate",
      headerName: "Data Demissão",
      width: 150,
      editable: editableFields.includes("dismissalDate"),
      type: "date"
    },
    {
      field: "startPriorNoticeDate",
      headerName: "Início Aviso Prévio",
      width: 170,
      editable: editableFields.includes("startPriorNoticeDate"),
      type: "date"
    },
    {
      field: "endPriorNoticeDate",
      headerName: "Término Aviso Prévio",
      width: 180,
      editable: editableFields.includes("endPriorNoticeDate"),
      type: "date"
    },
    {
      field: "transferred",
      headerName: "Transferido",
      width: 120,
      editable: false
    },
    { 
      field: "plan", 
      headerName: "Plano", 
      width: 150,
      editable: false
    },
    
    // Dependent specific fields
    { 
      field: "employeeId", 
      headerName: "ID Funcionário", 
      width: 130,
      editable: editableFields.includes("employeeId")
    },
    { 
      field: "dependentTypeId", 
      headerName: "Tipo Dependente", 
      width: 130,
      editable: editableFields.includes("dependentTypeId")
    },
    // { 
    //   field: "isDeleted", 
    //   headerName: "Excluído", 
    //   width: 100,
    //   editable: editableFields.includes("isDeleted")
    // },
    { 
      field: "isStudent", 
      headerName: "Estudante", 
      width: 100,
      editable: editableFields.includes("isStudent")
    },
    
    // Timestamps (usually not editable)
    // { 
    //   field: "createdAt", 
    //   headerName: "Criado em", 
    //   width: 150,
    //   editable: editableFields.includes("createdAt")
    // },
    // { 
    //   field: "updatedAt", 
    //   headerName: "Atualizado em", 
    //   width: 150,
    //   editable: editableFields.includes("updatedAt")
    // },
  ];

  async function handleSend() {
    try {
      setIsSendingUpdate(true);
      if (!changes) throw new Error("Nenhuma mudança identificada.");

      const payload = changes.map(change => {
        const result: any = {
          // Always include these identification fields
          taxPayerId: change?.taxPayerId?.replace(/\D/g, ""),
          type: change?.type === "Titular" ? "employee" : "dependent"
        };

        // Helper function to handle null values
        const getValue = (value: any) => value === null ? undefined : value;

        // Common fields
        if (change?.name?.newer !== undefined) result.name = getValue(change.name.newer);
        if (change?.taxPayerIdEmployee?.newer !== undefined) result.employeeTaxPayerId = change.taxPayerIdEmployee.newer?.replace(/\D/g, "") || undefined;
        if (change?.birthDate?.newer !== undefined) result.birthDate = getValue(change.birthDate.newer);
        if (change?.motherName?.newer !== undefined) result.motherName = getValue(change.motherName.newer);
        if (change?.weddingDate?.newer !== undefined) result.weddingDate = getValue(change.weddingDate.newer);

        // Employee specific fields
        if (change?.organizationId?.newer !== undefined) result.organizationId = getValue(change.organizationId.newer);
        if (change?.cnpj?.newer !== undefined) result.cnpj = getValue(change.cnpj.newer);
        if (change?.pis?.newer !== undefined) result.pis = getValue(change.pis.newer);
        if (change?.genderId?.newer !== undefined) result.genderId = getValue(change.genderId.newer);
        if (change?.raceId?.newer !== undefined) result.raceId = getValue(change.raceId.newer);
        if (change?.maritalStatusId?.newer !== undefined) result.maritalStatusId = getValue(change.maritalStatusId.newer);
        if (change?.educationLevelId?.newer !== undefined) result.educationLevelId = getValue(change.educationLevelId.newer);
        if (change?.socialName?.newer !== undefined) result.socialName = getValue(change.socialName.newer);
        if (change?.birthCountryId?.newer !== undefined) result.birthCountryId = getValue(change.birthCountryId.newer);
        if (change?.nationalityCountryId?.newer !== undefined) result.nationalityCountryId = getValue(change.nationalityCountryId.newer);
        if (change?.birthStateId?.newer !== undefined) result.birthStateId = getValue(change.birthStateId.newer);
        if (change?.birthCityId?.newer !== undefined) result.birthCityId = getValue(change.birthCityId.newer);
        if (change?.fatherName?.newer !== undefined) result.fatherName = getValue(change.fatherName.newer);
        if (change?.zipCode?.newer !== undefined) result.zipCode = getValue(change.zipCode.newer);
        if (change?.addressType?.newer !== undefined) result.addressType = getValue(change.addressType.newer);
        if (change?.address?.newer !== undefined) result.address = getValue(change.address.newer);
        if (change?.addressNumber?.newer !== undefined) result.addressNumber = getValue(change.addressNumber.newer);
        if (change?.complement?.newer !== undefined) result.complement = getValue(change.complement.newer);
        if (change?.neighborhood?.newer !== undefined) result.neighborhood = getValue(change.neighborhood.newer);
        if (change?.stateId?.newer !== undefined) result.stateId = getValue(change.stateId.newer);
        if (change?.cityId?.newer !== undefined) result.cityId = getValue(change.cityId.newer);
        if (change?.nationalIdentityNumber?.newer !== undefined) result.nationalIdentityNumber = getValue(change.nationalIdentityNumber.newer);
        if (change?.nationalIdentityEmissor?.newer !== undefined) result.nationalIdentityEmissor = getValue(change.nationalIdentityEmissor.newer);
        if (change?.nationalIdentityExpeditionDate?.newer !== undefined) result.nationalIdentityExpeditionDate = getValue(change.nationalIdentityExpeditionDate.newer);
        if (change?.phoneNumber?.newer !== undefined) result.phoneNumber = getValue(change.phoneNumber.newer);
        if (change?.email?.newer !== undefined) result.email = getValue(change.email.newer);
        if (change?.hiringDate?.newer !== undefined) result.hiringDate = getValue(change.hiringDate.newer);

        // New fields from layoutFields.txt
        if (change?.status?.newer !== undefined) result.status = getValue(change.status.newer);
        if (change?.jobTitle?.newer !== undefined) result.jobTitle = getValue(change.jobTitle.newer);
        if (change?.paymentFrequency?.newer !== undefined) result.paymentFrequency = getValue(change.paymentFrequency.newer);
        if (change?.grossPayment?.newer !== undefined) result.grossPayment = getValue(change.grossPayment.newer);
        if (change?.municipality?.newer !== undefined) result.municipality = getValue(change.municipality.newer);
        if (change?.cbo?.newer !== undefined) result.cbo = getValue(change.cbo.newer);
        if (change?.dismissalDate?.newer !== undefined) result.dismissalDate = getValue(change.dismissalDate.newer);
        if (change?.startPriorNoticeDate?.newer !== undefined) result.startPriorNoticeDate = getValue(change.startPriorNoticeDate.newer);
        if (change?.endPriorNoticeDate?.newer !== undefined) result.endPriorNoticeDate = getValue(change.endPriorNoticeDate.newer);
        if (change?.transferred?.newer !== undefined) result.transferred = getValue(change.transferred.newer);
        if (change?.plan?.newer !== undefined) result.plan = getValue(change.plan.newer);

        // Dependent specific fields
        if (change?.employeeId?.newer !== undefined) result.employeeId = getValue(change.employeeId.newer);
        if (change?.dependentTypeId?.newer !== undefined) result.dependentTypeId = getValue(change.dependentTypeId.newer);
        if (change?.isStudent?.newer !== undefined) {
          // Convert string back to boolean
          result.isStudent = change.isStudent.newer === 'Sim' ? true : change.isStudent.newer === 'Não' ? false : undefined;
        }

        return result;
      });

      console.log(changes)

      await api.put(`/v2/workspaces/${workspaceId}/edit-pjs`, {
        data: payload,
      })

      refetchData();
    } catch (error) {
      alert(error)
    } finally {
      setIsSendingUpdate(false);
      setIsModalOpen(false);
      setCurrentView('DEFAULT');
    }
  }

  function handleUndo() {
    setRealRows(data.map((row, index) => ({
      id: row?.id ?? `${index + 1}_${Date.now()}`,
      
      // Common fields
      name: row?.name ?? '',
      taxPayerId: !!row?.taxPayerId ? cpfMask(row.taxPayerId) : '',
      taxPayerIdEmployee: !!row?.employeeTaxPayerId ? cpfMask(row.employeeTaxPayerId) : '',
      birthDate: !!row?.birthDate ? new Date(row.birthDate) : null,
      motherName: row?.motherName ? row.motherName : '',
      weddingDate: !!row?.weddingDate ? new Date(row.weddingDate) : null,
      type: row?.type ?? '',
      
      // Employee specific fields
      organizationId: row?.organizationId ?? '',
      cnpj: row?.cnpj ?? '',
      pis: row?.pis ?? '',
      genderId: row?.genderId ?? '',
      raceId: row?.raceId ?? '',
      maritalStatusId: row?.maritalStatusId ?? '',
      educationLevelId: row?.educationLevelId ?? '',
      socialName: row?.socialName ?? '',
      birthCountryId: row?.birthCountryId ?? '',
      nationalityCountryId: row?.nationalityCountryId ?? '',
      birthStateId: row?.birthStateId ?? '',
      birthCityId: row?.birthCityId ?? '',
      fatherName: row?.fatherName ?? '',
      zipCode: row?.zipCode ?? '',
      addressType: row?.addressType ?? '',
      address: row?.address ?? '',
      addressNumber: row?.addressNumber ?? '',
      complement: row?.complement ?? '',
      neighborhood: row?.neighborhood ?? '',
      stateId: row?.stateId ?? '',
      cityId: row?.cityId ?? '',
      nationalIdentityNumber: row?.nationalIdentityNumber ?? '',
      nationalIdentityEmissor: row?.nationalIdentityEmissor ?? '',
      nationalIdentityExpeditionDate: !!row?.nationalIdentityExpeditionDate ? new Date(row.nationalIdentityExpeditionDate) : null,
      phoneNumber: row?.phoneNumber ?? '',
      email: row?.email ?? '',
      hiringDate: !!row?.hiringDate ? new Date(row.hiringDate) : null,
      
      // New fields from layoutFields.txt
      status: row?.status ?? '',
      jobTitle: row?.jobTitle ?? '',
      paymentFrequency: row?.paymentFrequency ?? '',
      grossPayment: row?.grossPayment ?? '',
      municipality: row?.municipality ?? '',
      cbo: row?.cbo ?? '',
      dismissalDate: !!row?.dismissalDate ? new Date(row.dismissalDate) : null,
      startPriorNoticeDate: !!row?.startPriorNoticeDate ? new Date(row.startPriorNoticeDate) : null,
      endPriorNoticeDate: !!row?.endPriorNoticeDate ? new Date(row.endPriorNoticeDate) : null,
      transferred: row?.transferred ?? '',
      plan: row?.plan ?? '',
      
      // Dependent specific fields
      employeeId: row?.employeeId ?? '',
      dependentTypeId: row?.dependentTypeId ?? '',
      isDeleted: row?.isDeleted !== null ? (row.isDeleted ? 'Sim' : 'Não') : '-',
      isStudent: row?.isStudent !== null ? (row.isStudent ? 'Sim' : 'Não') : '-',
      
      // Timestamps (mantendo '-' pois são apenas informativos)
      createdAt: !!row?.createdAt ? dayjs(row.createdAt).format("DD/MM/YYYY HH:mm") : '-',
      updatedAt: !!row?.updatedAt ? dayjs(row.updatedAt).format("DD/MM/YYYY HH:mm") : '-',
    })));
    setChanges(null);
  }

  return (
    <>
      <Modal open={isModalOpen} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
        <Box sx={{ position: "absolute" as "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", height: "auto", width: "100%", maxWidth: 1000, bgcolor: "background.paper", boxShadow: 24, borderRadius: "1rem", border: "none", outline: "none", padding: "24px" }}>
          <div style={{"display":"flex","justifyContent":"space-between","alignItems":"center"}}>
            <h6 style={{"padding":"0","margin":"0","fontSize":"1.25rem","lineHeight":"1.75rem","fontWeight":600}}>Salvar alterações?</h6>
            <Close style={{"cursor":"pointer"}} onClick={() => setIsModalOpen(false)}/>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '14px', marginTop: '24px' }}>
            <span style={{ fontSize: '16px', lineHeight: '175%', fontWeight: '400', color: '#191C1A' }}>Por favor, confira os dados antes de confirmar. As informações anteriores serão substituídas.</span>
          </div>
          <TableContainer sx={{ "& .MuiTableCell-root": { background: "#FFFFFF !important" } }}>
            <Table>
              <TableRow style={{ height: "70px" }}>
                <TableCell>
                  <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "rgb(111, 111, 120)", fontWeight: "400"}}>Nome completo</span>
                </TableCell>
                <TableCell>
                  <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "rgb(111, 111, 120)", fontWeight: "400" }}>CPF beneficiário</span>
                </TableCell>
                <TableCell>
                  <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "rgb(111, 111, 120)", fontWeight: "400" }}>Campos editados</span>
                </TableCell>
              </TableRow>
              {changes && changes.map(row => (
                <TableRow key={row.id} style={{ height: "70px" }}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.taxPayerId}</TableCell>
                  <TableCell>
                    {Object.keys(row)
                      .filter(key => !['id', 'name', 'taxPayerId', 'type'].includes(key) && row[key]?.field)
                      .map(key => row[key]?.field)
                      .join(", ")}
                  </TableCell>
                </TableRow>
              ))}
            </Table>
          </TableContainer>
          <div style={{"display":"flex","paddingTop":"0.5rem","paddingBottom":"0.5rem","marginTop":"1rem","justifyContent":"flex-end","alignItems":"center","width":"100%"}}>
            <div style={{"display":"flex","gap":"0.25rem"}}>
              <Button
                variant='outlined'
                color='error'
                style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                onClick={() => setIsModalOpen(false)}
                disabled={isSendingUpdate}
              >
                Cancelar
              </Button>
  
              <Button
                variant='contained'
                style={{ minWidth: "180px", "paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                onClick={handleSend}
                disabled={isSendingUpdate}
              >
                {!isSendingUpdate ? "Confirmar e Salvar" : <CircularProgress size="20px"/>}
              </Button>
            </div>
          </div>
        </Box>
      </Modal>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <IconButton>
          <ArrowBack color="primary" style={{ width: "25px" }} onClick={() => setCurrentView('DEFAULT')}/>
        </IconButton>
        <h1 style={{"padding":"0","marginTop":"12px","fontSize":"1.25rem","lineHeight":"1.75rem","fontWeight":600}}>Editar dados</h1>
      </div>

      <DataGrid
        rows={realRows}
        columns={realColumns}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
          // columns: {
          //   columnVisibilityModel: {
          //     // Hide some less common fields by default to improve UX
          //     organizationId: false,
          //     birthCountryId: false,
          //     nationalityCountryId: false,
          //     birthStateId: false,
          //     birthCityId: false,
          //     stateId: false,
          //     cityId: false,
          //     educationLevelId: false,
          //     raceId: false,
          //     addressType: false,
          //     nationalIdentityEmissor: false,
          //     nationalIdentityExpeditionDate: false,
          //     dependentTypeId: false,
          //     createdAt: false,
          //     updatedAt: false,
          //   },
          // },
        }}
        pageSizeOptions={[5, 10, 20, 50]}
        disableRowSelectionOnClick
        style={{ backgroundColor: "white", minHeight: 400 }}
        editMode="row"
        // Enable column filtering, sorting, and hiding
        disableColumnFilter={false}
        disableColumnSelector={false}
        disableDensitySelector={false}
        // Style non-editable cells to look disabled
        getCellClassName={(params) => {
          const isEditable = editableFields.includes(params.field);
          return isEditable ? '' : 'non-editable-cell';
        }}
        sx={{
          '& .non-editable-cell': {
            // backgroundColor: '#f5f5f5',
            color: '#9e9e9e',
            cursor: 'not-allowed',
            '&:hover': {
              backgroundColor: '#f0f0f0',
            },
          },
          '& .non-editable-cell .MuiDataGrid-cell': {
            fontStyle: 'italic',
          },
        }}
        //@ts-ignore
        processRowUpdate={(updatedRow, originalRow) => {
          // Update the rows array
          const newArr = [...realRows];
          const idx = newArr.findIndex(r => r.id === updatedRow.id);
          newArr[idx] = { ...updatedRow };
          
          // Find the original data for this row
          const originalData = data.find(row => 
            row?.taxPayerId?.replace(/\D/g, "") === updatedRow?.taxPayerId?.replace(/\D/g, "")
          );
          
          if (!originalData) {
            setRealRows(newArr);
            return updatedRow;
          }

          // Create change tracking object for this specific row
          const rowChanges: any = {
            id: updatedRow.id,
            name: updatedRow.name,
            taxPayerId: updatedRow.taxPayerId,
            type: updatedRow.type
          };

          let hasChanges = false;

          // Helper function to check if values are different
          const isDifferent = (newVal: any, oldVal: any, emptyValues = ['-', '', null, undefined]) => {
            // Handle Date objects
            if (newVal instanceof Date && oldVal instanceof Date) {
              return newVal.getTime() !== oldVal.getTime();
            }
            if (newVal instanceof Date && oldVal) {
              return newVal.getTime() !== new Date(oldVal).getTime();
            }
            if (oldVal instanceof Date && newVal) {
              return new Date(newVal).getTime() !== oldVal.getTime();
            }
            
            // Handle empty values
            if (emptyValues.includes(newVal) && emptyValues.includes(oldVal)) return false;
            return newVal !== oldVal;
          };

          // Check all possible fields for changes
          const fieldChecks = [
            // Common fields
            { field: 'name', label: 'Nome completo', newVal: updatedRow.name, oldVal: originalData.name },
            { field: 'taxPayerId', label: 'CPF beneficiário', newVal: updatedRow.taxPayerId, oldVal: originalData.taxPayerId ? cpfMask(originalData.taxPayerId) : '' },
            { field: 'taxPayerIdEmployee', label: 'CPF titular', newVal: updatedRow.taxPayerIdEmployee, oldVal: originalData.employeeTaxPayerId ? cpfMask(originalData.employeeTaxPayerId) : '' },
            { field: 'birthDate', label: 'Data de nascimento', newVal: updatedRow.birthDate, oldVal: originalData.birthDate },
            { field: 'motherName', label: 'Nome da mãe', newVal: updatedRow.motherName, oldVal: originalData.motherName },
            { field: 'weddingDate', label: 'Data de casamento', newVal: updatedRow.weddingDate, oldVal: originalData.weddingDate },
            { field: 'type', label: 'Tipo', newVal: updatedRow.type, oldVal: originalData.type },
            
            // Employee specific fields
            { field: 'organizationId', label: 'ID Organização', newVal: updatedRow.organizationId, oldVal: originalData.organizationId },
            { field: 'cnpj', label: 'CNPJ empresa', newVal: updatedRow.cnpj, oldVal: originalData.cnpj },
            { field: 'pis', label: 'PIS', newVal: updatedRow.pis, oldVal: originalData.pis },
            { field: 'genderId', label: 'Gênero', newVal: updatedRow.genderId, oldVal: originalData.genderId },
            { field: 'raceId', label: 'Raça', newVal: updatedRow.raceId, oldVal: originalData.raceId },
            { field: 'maritalStatusId', label: 'Estado Civil', newVal: updatedRow.maritalStatusId, oldVal: originalData.maritalStatusId },
            { field: 'educationLevelId', label: 'Escolaridade', newVal: updatedRow.educationLevelId, oldVal: originalData.educationLevelId },
            { field: 'socialName', label: 'Nome Social', newVal: updatedRow.socialName, oldVal: originalData.socialName },
            { field: 'birthCountryId', label: 'País Nascimento', newVal: updatedRow.birthCountryId, oldVal: originalData.birthCountryId },
            { field: 'nationalityCountryId', label: 'Nacionalidade', newVal: updatedRow.nationalityCountryId, oldVal: originalData.nationalityCountryId },
            { field: 'birthStateId', label: 'Estado Nascimento', newVal: updatedRow.birthStateId, oldVal: originalData.birthStateId },
            { field: 'birthCityId', label: 'Cidade Nascimento', newVal: updatedRow.birthCityId, oldVal: originalData.birthCityId },
            { field: 'fatherName', label: 'Nome do Pai', newVal: updatedRow.fatherName, oldVal: originalData.fatherName },
            { field: 'zipCode', label: 'CEP', newVal: updatedRow.zipCode, oldVal: originalData.zipCode },
            { field: 'addressType', label: 'Tipo Endereço', newVal: updatedRow.addressType, oldVal: originalData.addressType },
            { field: 'address', label: 'Endereço', newVal: updatedRow.address, oldVal: originalData.address },
            { field: 'addressNumber', label: 'Número', newVal: updatedRow.addressNumber, oldVal: originalData.addressNumber },
            { field: 'complement', label: 'Complemento', newVal: updatedRow.complement, oldVal: originalData.complement },
            { field: 'neighborhood', label: 'Bairro', newVal: updatedRow.neighborhood, oldVal: originalData.neighborhood },
            { field: 'stateId', label: 'Estado', newVal: updatedRow.stateId, oldVal: originalData.stateId },
            { field: 'cityId', label: 'Cidade', newVal: updatedRow.cityId, oldVal: originalData.cityId },
            { field: 'nationalIdentityNumber', label: 'RG', newVal: updatedRow.nationalIdentityNumber, oldVal: originalData.nationalIdentityNumber },
            { field: 'nationalIdentityEmissor', label: 'Órgão Emissor RG', newVal: updatedRow.nationalIdentityEmissor, oldVal: originalData.nationalIdentityEmissor },
            { field: 'nationalIdentityExpeditionDate', label: 'Data Expedição RG', newVal: updatedRow.nationalIdentityExpeditionDate, oldVal: originalData.nationalIdentityExpeditionDate },
            { field: 'phoneNumber', label: 'Telefone', newVal: updatedRow.phoneNumber, oldVal: originalData.phoneNumber },
            { field: 'email', label: 'E-mail', newVal: updatedRow.email, oldVal: originalData.email },
            { field: 'hiringDate', label: 'Data de admissão', newVal: updatedRow.hiringDate, oldVal: originalData.hiringDate },
            
            // New fields from layoutFields.txt
            { field: 'status', label: 'Status', newVal: updatedRow.status, oldVal: originalData.status },
            { field: 'jobTitle', label: 'Cargo', newVal: updatedRow.jobTitle, oldVal: originalData.jobTitle },
            { field: 'paymentFrequency', label: 'Periodicidade Pagamento', newVal: updatedRow.paymentFrequency, oldVal: originalData.paymentFrequency },
            { field: 'grossPayment', label: 'Pagamento Bruto', newVal: updatedRow.grossPayment, oldVal: originalData.grossPayment },
            { field: 'municipality', label: 'Município', newVal: updatedRow.municipality, oldVal: originalData.municipality },
            { field: 'cbo', label: 'CBO', newVal: updatedRow.cbo, oldVal: originalData.cbo },
            { field: 'dismissalDate', label: 'Data Demissão', newVal: updatedRow.dismissalDate, oldVal: originalData.dismissalDate },
            { field: 'transferred', label: 'Transferido', newVal: updatedRow.transferred, oldVal: originalData.transferred },
            { field: 'plan', label: 'Plano', newVal: updatedRow.plan, oldVal: originalData.plan },
            
            // Dependent specific fields
            { field: 'employeeId', label: 'ID Funcionário', newVal: updatedRow.employeeId, oldVal: originalData.employeeId },
            { field: 'dependentTypeId', label: 'Tipo Dependente', newVal: updatedRow.dependentTypeId, oldVal: originalData.dependentTypeId },
            { field: 'isStudent', label: 'Estudante', newVal: updatedRow.isStudent, oldVal: originalData.isStudent !== null ? (originalData.isStudent ? 'Sim' : 'Não') : '-' },
          ];

          fieldChecks.forEach(({ field, label, newVal, oldVal }) => {
            if (isDifferent(newVal, oldVal)) {
              rowChanges[field] = { old: oldVal, newer: newVal, field: label };
              hasChanges = true;
            }
          });

          // Update changes state
          if (hasChanges) {
            setChanges(prevChanges => {
              const existingChanges = prevChanges || [];
              const existingIndex = existingChanges.findIndex(c => c.id === updatedRow.id);
              
              if (existingIndex >= 0) {
                // Update existing change record
                const updatedChanges = [...existingChanges];
                updatedChanges[existingIndex] = rowChanges;
                return updatedChanges;
              } else {
                // Add new change record
                return [...existingChanges, rowChanges];
              }
            });
          } else {
            // Remove from changes if no changes detected
            setChanges(prevChanges => {
              if (!prevChanges) return null;
              const filtered = prevChanges.filter(c => c.id !== updatedRow.id);
              return filtered.length > 0 ? filtered : null;
            });
          }

          setRealRows(newArr);
          return updatedRow;
        }}
        onProcessRowUpdateError={() => {}}
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
      />

      <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'end', gap: "10px","paddingTop":"0.5rem","paddingBottom":"0.5rem","marginTop":"24px" }}>
        <Button
          variant='outlined'
          style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
          onClick={() => handleUndo()}
          disabled={!changes}
        >
          Descartar alterações
        </Button>
        <Button
          variant='contained'
          style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
          onClick={() => setIsModalOpen(true)}
          disabled={!changes}
        >
          Salvar
        </Button>
      </div>
    </>
  )
}
